import { supabase } from "@/integrations/supabase/client";

export type FrequencyWindow = { from: Date; to: Date };
export type EntityKey = "profiles" | "products" | "clients" | "suppliers" | "product_categories" | "expense_categories" | "transactions_entrada" | "transactions_saida" | "transaction_payments" | "bank_accounts" | "cash_register_sessions";

const EXPECTED_DAILY_RATES: Record<EntityKey, { min: number; max: number; weight: number }> = {
  profiles: { min: 0, max: 1, weight: 4 },
  products: { min: 1, max: 30, weight: 10 },
  clients: { min: 1, max: 20, weight: 9 },
  suppliers: { min: 0, max: 5, weight: 5 },
  product_categories: { min: 0, max: 2, weight: 3 },
  expense_categories: { min: 0, max: 2, weight: 3 },
  transactions_entrada: { min: 1, max: 200, weight: 12 },
  transactions_saida: { min: 0, max: 100, weight: 7 },
  transaction_payments: { min: 1, max: 200, weight: 12 },
  bank_accounts: { min: 0, max: 1, weight: 2 },
  cash_register_sessions: { min: 0, max: 10, weight: 6 },
};

export async function fetchTenantBase(tenantId: string) {
  const { data, error } = await supabase
    .from("tenants" as any)
    .select("id, name, created_at, subscription_status, subscription_next_billing_date, subscription_last_payment_date")
    .eq("id", tenantId)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data as any;
}

export async function fetchCountsByEntity(tenantId: string, window: FrequencyWindow) {
  const results: Record<EntityKey, number> = {
    profiles: 0,
    products: 0,
    clients: 0,
    suppliers: 0,
    product_categories: 0,
    expense_categories: 0,
    transactions_entrada: 0,
    transactions_saida: 0,
    transaction_payments: 0,
    bank_accounts: 0,
    cash_register_sessions: 0,
  };
  const run = async (table: string, key: EntityKey, extra?: { tipo?: string }) => {
    let q = (supabase as any).from(table).select("id", { count: "exact", head: true });
    if (table === "transactions" && extra?.tipo) q = (supabase.from("transactions") as any).select("id", { count: "exact", head: true }).eq("tipo", extra.tipo);
    const { count, error } = await q.eq("tenant_id", tenantId).gte("created_at", window.from.toISOString()).lte("created_at", window.to.toISOString());
    if (!error) results[key] = count || 0;
  };
  await Promise.all([
    run("profiles", "profiles"),
    run("products", "products"),
    run("clients", "clients"),
    run("suppliers", "suppliers"),
    run("product_categories", "product_categories"),
    run("expense_categories", "expense_categories"),
    run("transactions", "transactions_entrada", { tipo: "entrada" }),
    run("transactions", "transactions_saida", { tipo: "saida" }),
    run("transaction_payments", "transaction_payments"),
    run("bank_accounts", "bank_accounts"),
    run("cash_register_sessions", "cash_register_sessions"),
  ]);
  return results;
}

export async function fetchLongTermRates(tenantId: string) {
  const base = await fetchTenantBase(tenantId);
  const start = new Date(base.created_at);
  const now = new Date();
  const days = Math.max(1, Math.ceil((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
  const totals = await fetchCountsByEntity(tenantId, { from: start, to: now });
  const rates: Record<EntityKey, number> = Object.fromEntries(Object.entries(totals).map(([k, v]) => [k, Number(v) / days])) as any;
  return { days, rates } as { days: number; rates: Record<EntityKey, number> };
}

function scoreFrequencyDeviation(actualPerDay: number, expected: { min: number; max: number; weight: number }) {
  if (actualPerDay >= expected.min && actualPerDay <= expected.max) return 0;
  const dev = actualPerDay < expected.min ? (expected.min - actualPerDay) : (actualPerDay - expected.max);
  return Math.min(100, dev * expected.weight);
}

export function computeChurnScore(params: {
  subscription_status?: string | null;
  subscription_next_billing_date?: string | null;
  subscription_last_payment_date?: string | null;
  windowDays: number;
  windowCounts: Record<EntityKey, number>;
  longTermRates: Record<EntityKey, number>;
}) {
  const wDays = Math.max(1, params.windowDays);
  const perDay: Record<EntityKey, number> = Object.fromEntries(Object.entries(params.windowCounts).map(([k, v]) => [k, Number(v) / wDays])) as any;
  let score = 0;
  if (params.subscription_status === "cancelled" || params.subscription_status === "suspended") score += 50;
  const nextBilling = params.subscription_next_billing_date ? new Date(params.subscription_next_billing_date) : null;
  const now = new Date();
  if (!nextBilling || nextBilling < now) score += 10;
  const lastPayment = params.subscription_last_payment_date ? new Date(params.subscription_last_payment_date) : null;
  if (!lastPayment || (now.getTime() - lastPayment.getTime()) > 1000 * 60 * 60 * 24 * 30) score += 30;
  const activityTotal = (params.windowCounts.transactions_entrada || 0) + (params.windowCounts.transactions_saida || 0);
  if (activityTotal === 0) score += 20;
  Object.entries(EXPECTED_DAILY_RATES).forEach(([key, expected]) => {
    const actual = perDay[key as EntityKey] || 0;
    score += scoreFrequencyDeviation(actual, expected);
  });
  Object.entries(params.longTermRates).forEach(([key, avg]) => {
    const recent = perDay[key as EntityKey] || 0;
    if (avg > 0 && recent < avg * 0.5) score += 10;
    if (avg > 0 && recent > avg * 1.5) score = Math.max(0, score - 5);
  });
  return Math.min(100, Math.max(0, score));
}

export async function computeTenantHealth(tenantId: string, window: FrequencyWindow) {
  const base = await fetchTenantBase(tenantId);
  const counts = await fetchCountsByEntity(tenantId, window);
  const longTerm = await fetchLongTermRates(tenantId);
  const score = computeChurnScore({
    subscription_status: base.subscription_status,
    subscription_next_billing_date: base.subscription_next_billing_date,
    subscription_last_payment_date: base.subscription_last_payment_date,
    windowDays: Math.max(1, Math.ceil((window.to.getTime() - window.from.getTime()) / (1000 * 60 * 60 * 24)) + 1),
    windowCounts: counts,
    longTermRates: longTerm.rates,
  });
  const health = Math.max(0, 100 - score);
  return { tenant_id: tenantId, churn_score: score, health_score: health, window, counts, long_term: longTerm };
}

export async function upsertTenantHealth(tenantId: string, payload: any) {
  const { error } = await (supabase as any)
    .from("tenant_health")
    .upsert({ tenant_id: tenantId, metrics: payload, last_updated: new Date().toISOString() }, { onConflict: "tenant_id" });
  return !error;
}

export async function getStoredTenantHealth(tenantId: string) {
  const { data, error } = await (supabase as any)
    .from("tenant_health")
    .select("tenant_id, metrics, last_updated")
    .eq("tenant_id", tenantId)
    .limit(1)
    .maybeSingle();
  if (error) return null;
  return data as any;
}

